import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { NAV } from "@/lib/constants";

export default function Nav() {
  const { lang, t, setLang } = useI18n();
  const { user, logout } = useAuth();
  const { count } = useCart();
  const [, force] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const h = () => force((n) => n + 1);
    window.addEventListener("pv_lang_change", h);
    return () => window.removeEventListener("pv_lang_change", h);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-8 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#F7F7F6]/90 backdrop-blur-md border-b border-neutral-300"
          : "bg-transparent border-b border-neutral-300/40"
      }`}
    >
      <div className="max-w-[1600px] mx-auto flex items-center justify-between px-6 md:px-12 h-16">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L21 7V17L12 22L3 17V7L12 2Z" stroke="#111" strokeWidth="1.4" />
            <path d="M12 12L21 7M12 12L3 7M12 12V22" stroke="#111" strokeWidth="1.2" />
          </svg>
          <span className="font-serif-display text-xl leading-none tracking-tight">Peptivance</span>
          <span className="font-mono-label text-[10px] text-neutral-500 hidden md:inline">RESEARCH GRADE</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <Link
              key={n.key}
              to={n.to}
              data-testid={`nav-${n.key}`}
              className="font-mono-label text-[11px] text-neutral-700 hover:text-black relative group"
            >
              {t(`nav_${n.key}`)}
              <span className="absolute left-0 -bottom-1 h-px w-0 bg-black transition-[width] duration-500 group-hover:w-full" />
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div data-testid="lang-switcher" className="flex items-center gap-1 border border-neutral-300 bg-white/60">
            {["en", "nl", "de"].map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}`}
                onClick={() => setLang(l)}
                className={`font-mono-label text-[10px] px-2 py-1 transition-colors ${
                  lang === l ? "bg-black text-white" : "text-neutral-600 hover:text-black"
                }`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>

          {user ? (
            <div className="hidden md:flex items-center gap-4">
              <Link data-testid="nav-account" to={user.role === "admin" ? "/admin" : "/account"} className="font-mono-label text-[11px] hover:text-black">
                {user.role === "admin" ? t("admin_title") : t("nav_account")}
              </Link>
              <button data-testid="nav-logout" onClick={() => { logout(); navigate("/"); }} className="font-mono-label text-[11px] text-neutral-600 hover:text-black">
                {t("nav_logout")}
              </button>
            </div>
          ) : (
            <Link data-testid="nav-login" to="/login" className="hidden md:inline font-mono-label text-[11px] hover:text-black">
              {t("nav_login")}
            </Link>
          )}

          <Link
            to="/cart"
            data-testid="nav-cart"
            className="relative font-mono-label text-[11px] border border-black px-3 py-2 hover:bg-black hover:text-[#F7F7F6] transition-colors duration-300"
          >
            {t("nav_cart")} [{count}]
          </Link>
        </div>
      </div>
    </header>
  );
}
